const LOGO_URL = 'https://staffhub.wcdns.co.uk/rattlebone-logo.png';

export interface PrintQuestion {
  id: string;
  question: string;
  options: string[];
  correct: number | number[];
  explanation: string;
  type: string;
}

export interface PrintAnswer {
  id: string;
  answer: number | number[];
}

function isCorrectAnswer(q: PrintQuestion, a: PrintAnswer): boolean {
  if (Array.isArray(q.correct)) {
    const ua = Array.isArray(a.answer) ? a.answer : [a.answer as number];
    return JSON.stringify([...q.correct].sort()) === JSON.stringify([...ua].sort());
  }
  return a.answer === q.correct;
}

export function buildQuizResultHtml(params: {
  sectionTitle: string;
  completedAt?: string;
  score: number;
  total: number;
  percentage: number;
  questions: PrintQuestion[];
  answersArr: PrintAnswer[];
}): string {
  const { sectionTitle, completedAt, score, total, percentage, questions, answersArr } = params;

  const dateStr = completedAt
    ? new Date(completedAt).toLocaleString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })
    : new Date().toLocaleString('en-GB', { day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  const scoreColor = percentage >= 80 ? '#166534' : percentage >= 50 ? '#854d0e' : '#991b1b';
  const scoreBg = percentage >= 80 ? '#dcfce7' : percentage >= 50 ? '#fef9c3' : '#fee2e2';
  const scoreEmoji = percentage === 100 ? '🏆' : percentage >= 80 ? '🎉' : percentage >= 50 ? '👍' : '📚';

  const questionRows = questions.map((q, idx) => {
    const ans = answersArr.find(a => a.id === q.id);
    const userAnswerArr = !ans ? [] : Array.isArray(ans.answer) ? ans.answer as number[] : [ans.answer as number];
    const correctArr = Array.isArray(q.correct) ? q.correct as number[] : [q.correct as number];
    const correct = ans ? isCorrectAnswer(q, ans) : false;

    const optionsHtml = q.options.map((opt, i) => {
      const isRight = correctArr.includes(i);
      const wasSelected = userAnswerArr.includes(i);
      let bg = 'transparent'; let color = '#555'; let prefix = '○'; let weight = '400';
      if (isRight) { bg = '#dcfce7'; color = '#166534'; prefix = '✓'; weight = '600'; }
      else if (wasSelected && !isRight) { bg = '#fee2e2'; color = '#991b1b'; prefix = '✗'; weight = '600'; }
      return `<div style="padding:6px 10px;border-radius:4px;background:${bg};color:${color};font-weight:${weight};margin-bottom:4px;font-size:13px;">
        <span style="margin-right:8px;font-size:14px;">${prefix}</span>${opt}
        ${wasSelected && !isRight ? '<span style="margin-left:8px;font-size:11px;color:#991b1b;font-style:italic;">← your answer</span>' : ''}
      </div>`;
    }).join('');

    const statusBg = correct ? '#dcfce7' : '#fee2e2';
    const statusColor = correct ? '#166534' : '#991b1b';
    const statusIcon = correct ? '✓ Correct' : '✗ Incorrect';

    return `
      <div style="margin-bottom:24px;border:1px solid #e5e7eb;border-radius:8px;overflow:hidden;page-break-inside:avoid;">
        <div style="padding:12px 16px;background:${statusBg};border-bottom:1px solid #e5e7eb;display:flex;align-items:center;justify-content:space-between;">
          <span style="font-size:13px;font-weight:700;color:#1a1a1a;">Q${idx + 1}. ${q.question}</span>
          <span style="font-size:12px;font-weight:700;color:${statusColor};white-space:nowrap;margin-left:12px;">${statusIcon}</span>
        </div>
        <div style="padding:12px 16px;">
          ${optionsHtml}
          ${q.explanation ? `
            <div style="margin-top:12px;background:#eff6ff;border:1px solid #bfdbfe;border-radius:6px;padding:10px 14px;">
              <span style="font-size:12px;font-weight:700;color:#1d4ed8;display:block;margin-bottom:4px;">💡 Why this is the answer:</span>
              <span style="font-size:13px;color:#1e3a8a;line-height:1.5;">${q.explanation}</span>
            </div>
          ` : ''}
        </div>
      </div>`;
  }).join('');

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <title>Quiz Results: ${sectionTitle} — Rattlebone Staff Hub</title>
  <style>
    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Helvetica, Arial, sans-serif; color: #1a1a1a; background: #fff; line-height: 1.5; }
    .page { max-width: 760px; margin: 0 auto; padding: 48px 40px; }
    .header { display: flex; align-items: center; justify-content: space-between; padding-bottom: 20px; border-bottom: 3px solid #A36B58; margin-bottom: 32px; }
    .logo { height: 52px; width: auto; }
    .header-right { text-align: right; }
    .header-label { font-size: 13px; color: #888; text-transform: uppercase; letter-spacing: 0.06em; font-weight: 600; }
    .header-date { font-size: 12px; color: #aaa; margin-top: 2px; }
    .title-block { margin-bottom: 28px; }
    .section-title { font-size: 24px; font-weight: 700; color: #1a1a1a; margin-bottom: 4px; }
    .score-block { display: flex; align-items: center; gap: 20px; padding: 16px 20px; border-radius: 10px; background: ${scoreBg}; border: 1px solid ${scoreColor}30; margin-bottom: 32px; }
    .score-big { font-size: 40px; font-weight: 800; color: ${scoreColor}; line-height: 1; }
    .score-detail { color: #555; font-size: 14px; }
    .score-emoji { font-size: 36px; margin-left: auto; }
    .section-header { font-size: 11px; text-transform: uppercase; letter-spacing: 0.08em; color: #888; font-weight: 700; margin-bottom: 12px; padding-bottom: 6px; border-bottom: 1px solid #eee; }
    .footer { margin-top: 48px; padding-top: 16px; border-top: 1px solid #e5e7eb; display: flex; align-items: center; justify-content: space-between; }
    .footer-logo { height: 26px; opacity: 0.45; }
    .footer-text { font-size: 11px; color: #aaa; }
    @media print {
      body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
      @page { margin: 18mm 20mm; }
      .page { padding: 0; }
    }
  </style>
</head>
<body>
  <div class="page">
    <div class="header">
      <img class="logo" src="${LOGO_URL}" alt="The Rattlebone" />
      <div class="header-right">
        <div class="header-label">Quiz Results</div>
        <div class="header-date">${dateStr}</div>
      </div>
    </div>
    <div class="title-block">
      <h1 class="section-title">📝 ${sectionTitle}</h1>
      <p style="color:#666;font-size:14px;">Rattlebone Staff Hub — Knowledge Assessment</p>
    </div>
    <div class="score-block">
      <div>
        <div class="score-big">${percentage}%</div>
        <div class="score-detail">${score} out of ${total} correct</div>
        <div class="score-detail" style="margin-top:4px;">
          ${percentage >= 80 ? 'Great performance — keep it up!' : percentage >= 50 ? 'Good effort — review the explanations below and try again.' : 'Have another read through this section, then retake the quiz.'}
        </div>
      </div>
      <div class="score-emoji">${scoreEmoji}</div>
    </div>
    <div class="section-header">Question Breakdown — ${total} Questions</div>
    ${questionRows}
    <div class="footer">
      <img class="footer-logo" src="${LOGO_URL}" alt="Rattlebone" />
      <span class="footer-text">© Rattlebone Sherston Ltd ${new Date().getFullYear()} · Confidential — staff use only</span>
    </div>
  </div>
</body>
</html>`;
}

export function openPrintWindow(html: string): void {
  const win = window.open('', '_blank', 'width=920,height=780');
  if (!win) { alert('Please allow pop-ups to download/print results'); return; }
  win.document.write(html);
  win.document.close();
  setTimeout(() => { win.focus(); win.print(); }, 900);
}
